import {
  Bell,
  Bot,
  CreditCard,
  Gauge,
  KeyRound,
  LayoutDashboard,
  LifeBuoy,
  LogOut,
  MessageSquare,
  Monitor,
  Moon,
  Plug,
  ScrollText,
  Settings,
  Shield,
  ShieldCheck,
  Smartphone,
  Sun,
  Terminal,
  Users,
  Webhook,
} from 'lucide-react';
import { Fragment, useEffect, type ReactNode } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { auth, notifications as notificationsApi } from '../api/resources.js';
import { impersonation, session, setUnauthorizedHandler } from '../api/client.js';
import { adminApiResources, hasAdminMfaCode } from '../api/admin.js';
import { useAsync } from '../lib/hooks.js';
import { NotificationBell } from './notification-bell.js';
import {
  Sidebar,
  SidebarBrand,
  SidebarItem,
  SidebarProvider,
  SidebarSection,
  SidebarTrigger,
  useSidebar,
} from './sidebar.js';
import { useTheme, type Theme } from './theme.js';
import { Avatar, AvatarFallback, initialsOf } from './ui/avatar.js';
import { Button } from './ui/button.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu.js';
import { TooltipProvider } from './ui/tooltip.js';

/** Console navigation (PRD §28). */
interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  end?: boolean;
}

const NAV: Array<{ section: string | null; items: NavItem[] }> = [
  {
    section: null,
    items: [{ to: '/', label: 'Visão geral', icon: <LayoutDashboard />, end: true }],
  },
  {
    section: 'Operação',
    items: [
      { to: '/instances', label: 'Instâncias', icon: <Smartphone /> },
      { to: '/messages', label: 'Mensagens', icon: <MessageSquare /> },
      { to: '/webhooks', label: 'Webhooks', icon: <Webhook /> },
    ],
  },
  {
    section: 'Desenvolvedores',
    items: [
      { to: '/developers/keys', label: 'API Keys', icon: <KeyRound /> },
      { to: '/developers/logs', label: 'Logs da API', icon: <ScrollText /> },
      { to: '/developers/explorer', label: 'API Explorer', icon: <Terminal /> },
      { to: '/developers/mcp', label: 'MCP', icon: <Bot /> },
      { to: '/integrations', label: 'Integrações', icon: <Plug /> },
    ],
  },
  {
    section: 'Conta',
    items: [
      { to: '/usage', label: 'Consumo', icon: <Gauge /> },
      { to: '/billing', label: 'Plano e cobrança', icon: <CreditCard /> },
      { to: '/team', label: 'Equipe', icon: <Users /> },
      { to: '/notifications', label: 'Notificações', icon: <Bell /> },
      { to: '/audit', label: 'Auditoria', icon: <ShieldCheck /> },
      { to: '/support', label: 'Suporte', icon: <LifeBuoy /> },
      { to: '/settings', label: 'Configurações', icon: <Settings /> },
    ],
  },
];

/** Breadcrumb por rota: seção (quando há) e página. */
function crumbsFor(pathname: string): Array<{ label: string; to?: string }> {
  for (const group of NAV) {
    for (const item of group.items) {
      const match = item.end ? pathname === item.to : pathname.startsWith(item.to);
      if (match) {
        const crumbs = group.section ? [{ label: group.section }] : [];
        if (pathname === item.to) return [...crumbs, { label: item.label }];
        return [...crumbs, { label: item.label, to: item.to }, { label: 'Detalhe' }];
      }
    }
  }
  return [{ label: 'Talkium' }];
}

export function Shell() {
  return (
    <SidebarProvider>
      <TooltipProvider delayDuration={300}>
        <ShellFrame />
      </TooltipProvider>
    </SidebarProvider>
  );
}

function ShellFrame() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // A 401 anywhere in the app returns to login rather than leaving a dead screen.
    setUnauthorizedHandler(() => {
      session.clear();
      navigate('/login', { replace: true });
    });
  }, [navigate]);

  // Uma leitura alimenta o sino e o badge da sidebar; marcar como lida recarrega as duas.
  const inbox = useAsync(async () => (await notificationsApi.list()).data, [location.pathname]);

  /**
   * Uma chamada só responde às duas perguntas do cabeçalho: quem é quem está aqui, e
   * em nome de quem. Duas chamadas separadas poderiam divergir por um instante, e é
   * justamente nesse instante que o banner de §65 não pode faltar.
   */
  const me = useAsync(() => auth.me(), [location.pathname]);
  const grant = me.data?.impersonation ?? null;
  const crumbs = crumbsFor(location.pathname);
  const unreadCount = (inbox.data ?? []).filter((item) => item.readAt === null).length;

  const leaveImpersonation = () => {
    /**
     * A ordem importa. Apagar o token é o que de fato encerra o acesso: ele só existe
     * aqui, nesta aba, e não há rota que o releia — destruí-lo é irreversível e não
     * depende de a rede colaborar.
     *
     * Encerrar a CONCESSÃO no servidor é arrumação, e por isso é a segunda coisa e não
     * pode atrapalhar a primeira. Só acontece se o operador ainda tiver um código de
     * segundo fator em mãos: pedir um código novo só para sair transformaria o caminho
     * de volta num obstáculo. A concessão que sobrevive a isso expira sozinha.
     */
    impersonation.leave();
    if (grant && hasAdminMfaCode()) {
      void adminApiResources.endImpersonation(grant.id).catch(() => undefined);
    }
    navigate('/admin', { replace: true });
  };

  const logout = async () => {
    await auth.logout().catch(() => undefined);
    session.clear();
    navigate('/login', { replace: true });
  };

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar label="Navegação principal">
        <SidebarBrand name="Talkium" />

        {/*
          O link para o plano de operação (§58) só aparece para quem é da operação. Isso é
          ergonomia, não controle de acesso: quem decide é o `authenticateAdmin`, que relê
          o usuário e ainda exige o segundo fator. Um cliente que digitar `/admin` na
          barra chega à mesma parede.
        */}
        {me.data?.platformAdmin === true && (
          <SidebarItem
            to="/admin"
            label="Admin"
            icon={<Shield />}
            tag={<span className="ops-tag">Operação</span>}
          />
        )}

        {NAV.map((group) => (
          <SidebarSection key={group.section ?? 'root'} label={group.section ?? undefined}>
            {group.items.map((item) => (
              <SidebarItem
                key={item.to}
                to={item.to}
                end={item.end}
                icon={item.icon}
                label={item.label}
                badge={
                  item.to === '/notifications' && unreadCount > 0 ? (
                    <span className="rounded-full bg-danger px-1.5 text-[11px] font-semibold text-white">
                      {unreadCount}
                    </span>
                  ) : undefined
                }
              />
            ))}
          </SidebarSection>
        ))}
      </Sidebar>

      <div className="flex min-w-0 flex-1 flex-col">
        {grant && <ImpersonationBanner grant={grant} onLeave={leaveImpersonation} />}

        <header className="sticky top-0 z-10 flex h-14 shrink-0 items-center gap-2 border-b border-border bg-card/80 px-4 backdrop-blur">
          <SidebarTrigger className="-ml-1" />
          <div className="mx-1 h-4 w-px bg-border" aria-hidden />
          <Breadcrumbs crumbs={crumbs} />

          <div className="ml-auto flex items-center gap-1">
            <NotificationBell items={inbox.data ?? []} onChange={inbox.reload} />
            <ThemeToggle />
            <UserMenu
              name={me.data?.user?.name}
              email={me.data?.user?.email}
              role={me.data?.role}
              impersonating={grant !== null}
              onLogout={logout}
            />
          </div>
        </header>

        <main className="flex w-full max-w-[1280px] flex-1 flex-col gap-5 p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function Breadcrumbs({ crumbs }: { crumbs: Array<{ label: string; to?: string }> }) {
  return (
    <nav aria-label="Você está em" className="flex min-w-0 items-center gap-1.5 text-sm">
      <Link to="/" className="hidden text-muted-foreground hover:text-foreground md:inline">
        Talkium
      </Link>
      {crumbs.map((crumb, index) => {
        const last = index === crumbs.length - 1;
        return (
          <Fragment key={`${crumb.label}-${index}`}>
            <span className="hidden text-subtle md:inline" aria-hidden>
              /
            </span>
            {crumb.to && !last ? (
              <Link to={crumb.to} className="truncate text-muted-foreground hover:text-foreground">
                {crumb.label}
              </Link>
            ) : (
              <h1
                className={`m-0 truncate text-sm ${last ? 'font-semibold text-foreground' : 'font-normal text-muted-foreground'}`}
                aria-current={last ? 'page' : undefined}
              >
                {crumb.label}
              </h1>
            )}
          </Fragment>
        );
      })}
    </nav>
  );
}

function ThemeToggle() {
  const { theme, resolved, setTheme } = useTheme();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon-sm" aria-label="Tema">
          {resolved === 'dark' ? <Moon /> : <Sun />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuRadioGroup value={theme} onValueChange={(value) => setTheme(value as Theme)}>
          <DropdownMenuRadioItem value="system">
            <Monitor /> Tema do sistema
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="light">
            <Sun /> Claro
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="dark">
            <Moon /> Escuro
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function UserMenu({
  name,
  email,
  role,
  impersonating,
  onLogout,
}: {
  name?: string;
  email?: string;
  role?: string;
  impersonating: boolean;
  onLogout: () => void;
}) {
  const { isMobile } = useSidebar();
  const display = name || email?.split('@')[0] || 'Você';
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Menu do usuário"
          className="ml-1 rounded-full"
        >
          <Avatar>
            <AvatarFallback>{initialsOf(name || email)}</AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" side={isMobile ? 'bottom' : 'bottom'} className="w-60">
        <DropdownMenuLabel className="p-0 font-normal">
          <div className="flex items-center gap-2 px-2 py-1.5">
            <Avatar>
              <AvatarFallback>{initialsOf(name || email)}</AvatarFallback>
            </Avatar>
            <div className="grid min-w-0 flex-1 text-left leading-tight">
              <span className="truncate text-sm font-medium text-foreground">{display}</span>
              {email && <span className="truncate text-xs text-muted-foreground">{email}</span>}
              {role && <span className="text-[11px] text-subtle">{role}</span>}
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/settings" className="text-foreground no-underline hover:no-underline">
            <Settings /> Configurações
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/team" className="text-foreground no-underline hover:no-underline">
            <Users /> Equipe
          </Link>
        </DropdownMenuItem>
        {/*
          Sob impersonation, "Sair" encerraria a sessão do OPERADOR, que não é o que quem
          clica quer — quem está dentro da conta de um cliente quer voltar para a sua. O
          caminho de volta é o banner, que está sempre visível.
        */}
        {!impersonating && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem destructive onSelect={onLogout}>
              <LogOut /> Sair
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * O banner de §65.
 *
 * §65 pede que a impersonation seja visível. Visível quer dizer três coisas concretas:
 * em toda tela, sem depender de lembrar, e com o caminho de volta ao alcance da mão —
 * por isso ele mora no shell, acima do conteúdo, e carrega o botão de sair.
 *
 * Ele também diz o MOTIVO que o operador escreveu. Não é decoração: é o que ele lê
 * quando, vinte minutos depois, estiver olhando uma tela e não lembrar por que entrou.
 */
function ImpersonationBanner({
  grant,
  onLeave,
}: {
  grant: { id: string; reason: string; expiresAt: string; readOnly: boolean };
  onLeave: () => void;
}) {
  const expires = new Date(grant.expiresAt);
  const minutesLeft = Math.max(0, Math.round((expires.getTime() - Date.now()) / 60000));

  return (
    <div
      className="flex items-center justify-between gap-3 border-b border-warning bg-warning-soft px-5 py-2.5 text-[13px] text-warning"
      role="status"
    >
      <div>
        <strong>Você está vendo a conta de um cliente.</strong>{' '}
        {grant.readOnly && 'Somente leitura — nada aqui pode ser alterado. '}
        Expira em {minutesLeft} min. Motivo:{' '}
        <em className="not-italic opacity-85">{grant.reason}</em>
      </div>
      <Button variant="outline" size="sm" onClick={onLeave}>
        Sair da impersonation
      </Button>
    </div>
  );
}
